import React from 'react';
import { FaUndo, FaClock, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaCalendarAlt, FaBox, FaTag, FaDollarSign } from 'react-icons/fa';

const ReturnDetailsRenderer = ({ data }) => {
  if (!data) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 text-gray-600">
          <FaUndo className="text-sm" />
          <span className="text-sm">No return details found</span>
        </div>
      </div>
    );
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PENDING':
        return <FaClock className="text-yellow-500" />;
      case 'APPROVED':
        return <FaCheckCircle className="text-green-500" />;
      case 'REJECTED':
        return <FaTimesCircle className="text-red-500" />;
      case 'PROCESSED':
        return <FaBox className="text-blue-500" />;
      default:
        return <FaExclamationTriangle className="text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      case 'PROCESSED':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div className="space-y-6">
      {data.message && (
        <div className="text-sm text-gray-600 mb-4">
          {data.message}
        </div>
      )}
      
      {/* Return Header */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <FaUndo className="text-blue-500 text-lg" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Return Request Details</h2>
              <p className="text-sm text-gray-500">ID: {data.id}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {getStatusIcon(data.approvalStatus)}
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(data.approvalStatus)}`}>
              {data.approvalStatus}
            </span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <FaCalendarAlt className="text-gray-400 text-sm" />
              <div>
                <span className="text-sm text-gray-500">Created</span>
                <div className="font-medium text-gray-900">{formatDate(data.metadata?.createdAt)}</div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <FaCalendarAlt className="text-gray-400 text-sm" />
              <div>
                <span className="text-sm text-gray-500">Expires</span>
                <div className="font-medium text-gray-900">{formatDate(data.expiryDate)}</div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <FaBox className="text-gray-400 text-sm" />
              <div>
                <span className="text-sm text-gray-500">Received</span>
                <div className="font-medium text-gray-900">{data.received ? 'Yes' : 'No'}</div>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <FaDollarSign className="text-gray-400 text-sm" />
              <div>
                <span className="text-sm text-gray-500">Total Value</span>
                <div className="font-semibold text-lg text-gray-900">
                  {data.total?.currency} ${data.total?.value?.toFixed(2) || '0.00'}
                </div>
              </div>
            </div>
            
            {data.metadata?.version && (
              <div className="flex items-center space-x-3">
                <FaTag className="text-gray-400 text-sm" />
                <div>
                  <span className="text-sm text-gray-500">Version</span>
                  <div className="font-medium text-gray-900">v{data.metadata.version}</div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {data.reason && (
          <div className="border-t border-gray-100 pt-4 mt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Return Reason</h3>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-sm text-gray-700">
                {data.reason.details || data.reason.code}
              </div>
              {data.reason.code && (
                <div className="text-xs text-gray-500 mt-1">Code: {data.reason.code}</div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Orders and Items */}
      {data.orders && data.orders.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Items to Return</h3>
          
          <div className="space-y-4">
            {data.orders.map((order, orderIndex) => (
              <div key={order.id || orderIndex} className="border border-gray-100 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <FaBox className="text-gray-400 text-sm" />
                  <h4 className="font-medium text-gray-900">Order {order.id}</h4>
                </div>
                
                <div className="space-y-3">
                  {order.items.map((item, itemIndex) => (
                    <div key={item.id || itemIndex} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div>
                            <h5 className="font-medium text-gray-900">{item.name}</h5>
                            <p className="text-sm text-gray-500">ID: {item.id}</p>
                          </div>
                        </div>
                        
                        <div className="mt-2 flex items-center space-x-4 text-sm text-gray-600">
                          <span>Qty: {item.quantity}</span>
                          <span>Unit: {item.unitPrice?.currency} ${item.unitPrice?.value?.toFixed(2) || '0.00'}</span>
                        </div>
                        
                        {item.reason && (
                          <div className="mt-2">
                            <span className="text-xs text-gray-500">Reason: </span>
                            <span className="text-xs text-gray-700">
                              {item.reason.details || item.reason.code}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="text-right">
                        <div className="font-semibold text-gray-900">
                          {item.total?.currency} ${item.total?.value?.toFixed(2) || '0.00'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReturnDetailsRenderer;
